import ReactDOM from 'react-dom';
import LoadingIndicator from '../../general/loading/LoadingIndicator';
import { useIframeContext } from '../../../context/IframeContext';
import { useContext, useEffect, useState } from 'react';
import { MetricMetadata, PercentileData } from '../../../services/types';
import { fetchMetricMetadataByOrg, fetchPercentileDist, orgId } from '../../../utils/dashboardUtils';
import MetricCategoriesContext from '../../../context/MetricCategoriesContext';
import MappableMetadataCard from './MappableMetadataCard';

interface MappableMetricsModalProps {
    isOpen: boolean;
    onClose: () => void;
}

const MappableMetricsModal: React.FC<MappableMetricsModalProps> = ({ isOpen, onClose }) => {
    const { isIframe } = useIframeContext();
    const { metricCategories } = useContext(MetricCategoriesContext);

    const [metadata, setMetadata] = useState<MetricMetadata[] | null>(null);
    const [percentileData, setPercentileData] = useState<PercentileData[] | null>(null)
    const [loading, setLoading] = useState<boolean>(false);

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);

            try {
                // Fetch mappable metadata
                if (!metadata) {
                    const data = await fetchMetricMetadataByOrg(orgId);
                    const mappableMetrics = data?.data?.filter((metric: MetricMetadata) => metric.mappable) || [];
                    setMetadata(mappableMetrics);
                }

                // Fetch percentile data
                if (!percentileData) {
                    const percentileResults = await Promise.all(
                        metricCategories.map((category) => fetchPercentileDist(orgId, category))
                    );
                    const flattenedPercentiles = percentileResults.flatMap((res) => res?.data || []);
                    setPercentileData(flattenedPercentiles);
                }
            } catch (error) {
                console.error('Error fetching metadata or percentile data:', error);
            } finally {
                setLoading(false);
            }
        };

        if (isOpen && orgId) fetchData();
    }, [isOpen, orgId, metricCategories, metadata, percentileData]);

    // Hide main scrollbar when the metadata panel is open
    useEffect(() => {
        document.body.style.overflow = isOpen ? 'hidden' : '';
        return () => {
            document.body.style.overflow = '';
        };
    }, [isOpen]);

    if (!isOpen) return null;

    const groupedMetadata = metricCategories.reduce((acc, category) => {
        const categoryMetrics = metadata?.filter((metric) => metric.metric_category === category) || [];
        const mappableMetrics = categoryMetrics.filter((metric) =>
            percentileData?.some((p) => Number(p.id) === Number(metric.id))
        );

        if (mappableMetrics.length > 0) {
            acc[category] = mappableMetrics;
        }

        return acc;
    }, {} as Record<string, MetricMetadata[]>);

    return ReactDOM.createPortal(
        <div className={`fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-50 transition-all duration-500 transform ${!isOpen ? 'translate-y-full' : 'translate-y-0'} ${!isIframe && 'max-w-full mx-auto'}`}>
            <div className={`bg-white p-6 w-full overflow-y-auto h-full relative transition-transform duration-500 transform ${!isOpen ? 'translate-y-full' : 'translate-y-0'}`}>
                <button
                    className="absolute top-3 right-5 text-gray-600 text-3xl font-bold hover:text-black"
                    onClick={onClose}
                    aria-label="Close modal"
                >
                    &times;
                </button>
                {loading ? (
                    <LoadingIndicator />
                ) : metadata && metadata.length > 0 ? (
                    <div className="">
                        <div className="flex items-center justify-end text-xs font-bold text-color-cai-gray mr-16">
                            <div className="flex items-center w-24">
                                <span className="text-lg">🌟</span>
                                <span>Featured</span>
                            </div>
                            <div className="flex items-center w-24">
                                <span className="text-lg">🏠</span>
                                <span>Default</span>
                            </div>
                        </div>

                        {Object.entries(groupedMetadata).map(([category, metrics]) => (
                            <div key={category} className='border-b border-black py-8'>
                                <h2 className="text-lg font-bold text-color-cai-gray mb-4 mx-2">{category}</h2>
                                <div className="grid grid-cols-4 gap-4">
                                    {metrics.map((metric) => {
                                        const metricData =
                                            percentileData?.find((p) => Number(p.id) === Number(metric.id)) || {
                                                id: metric.id,
                                                name: metric.metric_name,
                                                versions: [],
                                            };

                                        return (
                                            <MappableMetadataCard
                                                key={metric.id}
                                                metadata={metric}
                                                metric={metricData}
                                                width="w-full"
                                            />
                                        );
                                    })}
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <p>No metadata available.</p>
                )}
            </div>
        </div>,
        document.body
    );
};

export default MappableMetricsModal;